import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  ExternalLink, Calendar, ArrowRight, Globe, Newspaper, 
  BookOpen, Mic, Pin, Loader2, FileText
} from "lucide-react";
import { Badge } from "./ui/badge";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESOURCE_ICONS = {
  article: Newspaper,
  publication: BookOpen,
  news: Globe,
  media: Mic
};

const ResourcesInsights = () => {
  const [resources, setResources] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loadingResources, setLoadingResources] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchResources();
    fetchBlogPosts();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchBlogPosts();
  }, [selectedCategory]);

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/resources/`);
      setResources(response.data.resources || []);
    } catch (error) {
      console.error("Error fetching resources:", error);
    } finally {
      setLoadingResources(false);
    }
  };

  const fetchBlogPosts = async () => {
    setLoadingPosts(true);
    try {
      const params = selectedCategory !== "all" ? `?category=${selectedCategory}&limit=10` : "?limit=10";
      const response = await axios.get(`${API}/blog/posts${params}`);
      setBlogPosts(response.data.posts || []);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
    } finally {
      setLoadingPosts(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/blog/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  return (
    <section className="py-16 bg-slate-50" id="resources-insights">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-heading font-bold text-primary mb-4">
            Resources & Insights
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Expert perspectives and supporting information on Compliance, Quality, Food Safety and Cybersecurity.
          </p>
        </div>

        {/* Main Content - Sidebar Left, Blog Right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* LEFT SIDEBAR - External Resources */}
          <div className="lg:col-span-4">
            <div className="bg-white rounded-xl shadow-sm border p-6 sticky top-24">
              <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                <Globe className="h-5 w-5 text-brand" />
                In the Media & Publications
              </h3>
              
              {loadingResources ? (
                <div className="text-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand" />
                </div>
              ) : resources.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Featured resources coming soon.
                </p>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                  {resources.map((resource) => {
                    const Icon = RESOURCE_ICONS[resource.resource_type] || Globe;
                    return (
                      <a
                        key={resource.id}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 rounded-lg border border-slate-100 hover:border-brand/30 hover:bg-brand/5 transition-all group"
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-1.5 rounded bg-slate-100 group-hover:bg-brand/10">
                            <Icon className="h-4 w-4 text-slate-500 group-hover:text-brand" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="font-medium text-sm text-slate-800 group-hover:text-brand line-clamp-2">
                                {resource.title}
                              </h4>
                              {resource.is_pinned && (
                                <Pin className="h-3 w-3 text-amber-500 flex-shrink-0 mt-1" />
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {resource.source}
                              {resource.publication_date && (
                                <> • {new Date(resource.publication_date).toLocaleDateString()}</>
                              )}
                            </p>
                          </div>
                          <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-brand flex-shrink-0" />
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT MAIN CONTENT - Blog Posts */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5 text-brand" />
                  Blog & Insights
                </h3>
                
                {/* Category Filter */}
                {categories.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => setSelectedCategory("all")}
                      className={`px-3 py-1 text-sm rounded-full transition-colors ${
                        selectedCategory === "all"
                          ? "bg-brand text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      All
                    </button>
                    {categories.slice(0, 4).map((cat) => (
                      <button
                        key={cat.name}
                        onClick={() => setSelectedCategory(cat.name)}
                        className={`px-3 py-1 text-sm rounded-full transition-colors ${
                          selectedCategory === cat.name
                            ? "bg-brand text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {loadingPosts ? (
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-brand" />
                  <p className="text-muted-foreground mt-2">Loading posts...</p>
                </div>
              ) : blogPosts.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h4 className="font-semibold text-lg mb-2">Coming Soon</h4>
                  <p className="text-muted-foreground">
                    Expert insights and articles will be published here shortly.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {blogPosts.map((post) => (
                    <article 
                      key={post.id} 
                      className="group border-b border-slate-100 pb-6 last:border-0 last:pb-0"
                    >
                      <div className="flex gap-4">
                        {post.featured_image && (
                          <Link to={`/tools/resources/${post.slug}`} className="flex-shrink-0">
                            <img
                              src={post.featured_image}
                              alt={post.title}
                              className="w-32 h-24 object-cover rounded-lg"
                            />
                          </Link>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-xs">
                              {post.category}
                            </Badge>
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(post.publish_date).toLocaleDateString()}
                            </span>
                          </div>
                          <Link to={`/tools/resources/${post.slug}`}>
                            <h4 className="font-semibold text-lg mb-2 group-hover:text-brand transition-colors line-clamp-2">
                              {post.title}
                            </h4>
                          </Link>
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                            {post.excerpt}
                          </p>
                          <Link 
                            to={`/tools/resources/${post.slug}`}
                            className="inline-flex items-center text-sm font-medium text-brand hover:underline"
                          >
                            Read more
                            <ArrowRight className="h-4 w-4 ml-1" />
                          </Link>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResourcesInsights;
